import React, { useState } from 'react'
import { Table, Button, Dropdown, Menu, Checkbox, Pagination } from 'antd'
import { PlusOutlined, MoreOutlined } from '@ant-design/icons'
import './SimpleTable.css'
import Loader from '../Loader/Loader'

// Status Pill Component
const StatusPill = ({ status, children }) => {
  const statusClass = status?.toLowerCase() || 'inactive'
  return (
    <span className={`status-pill ${statusClass}`}>
      {children || status}
    </span>
  )
}

const SimpleTable = ({
  title = "Table",
  data = [],
  columns = [],
  onAdd,
  onEdit,
  onDelete,
  onSelect,
  selectedRowKeys = [],
  pagination = true,
  pageSize = 10,
  showCheckbox = true,
  showActions = true,
  className = "",
  loading = false,
  deleteButtonText = "Delete"
}) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedKeys, setSelectedKeys] = useState(selectedRowKeys)

  const handleSelectChange = (selectedRowKeys, selectedRows) => {
    setSelectedKeys(selectedRowKeys)
    if (onSelect) {
      onSelect(selectedRowKeys, selectedRows)
    }
  }

  const getActionMenu = (record) => {
    // Determine button text based on status
    const getButtonText = () => {
      if (typeof deleteButtonText === 'function') {
        return deleteButtonText(record)
      }
      return deleteButtonText
    }

    return (
      <Menu>
        <Menu.Item key="edit" onClick={() => onEdit && onEdit(record)}>
          Edit
        </Menu.Item>
        <Menu.Item key="delete" onClick={() => onDelete && onDelete(record)}>
          {getButtonText()}
        </Menu.Item>
      </Menu>
    )
  }

  const tableColumns = [
    ...(showCheckbox ? [{
      title: '',
      dataIndex: 'checkbox',
      key: 'checkbox',
      width: 50,
      render: (_, record) => (
        <Checkbox
          checked={selectedKeys.includes(record.key)}
          onChange={(e) => {
            if (e.target.checked) {
              handleSelectChange([...selectedKeys, record.key], [...data.filter(item => selectedKeys.includes(item.key)), record])
            } else {
              handleSelectChange(selectedKeys.filter(key => key !== record.key), data.filter(item => selectedKeys.includes(item.key) && item.key !== record.key))
            }
          }}
        />
      )
    }] : []),
    ...columns,
    ...(showActions ? [{
      title: '',
      key: 'actions',
      width: 50,
      render: (_, record) => (
        <Dropdown overlay={getActionMenu(record)} trigger={['click']}>
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      )
    }] : [])
  ]

  const startIndex = (currentPage - 1) * pageSize
  const endIndex = startIndex + pageSize
  const currentData = pagination ? data.slice(startIndex, endIndex) : data

  return (
    <div className={`bg-white rounded-lg border border-gray-200 ${className}`}>
      {/* Table with integrated header */}
      <div className="overflow-x-auto">
        <table className="w-full">
          {/* Table title and add button in first row */}
          <thead>
            <tr className="bg-white border-b border-gray-200">
              <td colSpan={tableColumns.length} className="px-4 py-3">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
                  {onAdd && (
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={onAdd}
                      className="bg-orange-500 hover:bg-orange-600 border-orange-500 hover:border-orange-600"
                    >
                      Add
                    </Button>
                  )}
                </div>
              </td>
            </tr>
            {/* Column headers */}
            <tr className="bg-gray-50 border-b border-gray-200">
              {tableColumns.map((column, index) => (
                <th
                  key={index}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700"
                  style={{ width: column.width }}
                >
                  {column.title}
                </th>
              ))}
            </tr>
          </thead>
          {loading ? (<Loader />) : <tbody>
            {currentData.map((record, rowIndex) => (
              <tr key={record.key || rowIndex} className="border-b border-gray-100 hover:bg-gray-50">
                {tableColumns.map((column, colIndex) => (
                  <td
                    key={colIndex}
                    className="px-4 py-3 text-xs text-gray-900"
                  >
                    {column.render ? column.render(record[column.dataIndex], record, rowIndex) : record[column.dataIndex]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>}
        </table>
      </div>

      {/* Pagination */}
      {pagination && data.length > pageSize && (
        <div className="flex justify-center mt-4 p-4 border-t border-gray-200">
          <Pagination
            current={currentPage}
            total={data.length}
            pageSize={pageSize}
            onChange={setCurrentPage}
            showSizeChanger={false}
            className="custom-pagination"
          />
        </div>
      )}
    </div>
  )
}

export default SimpleTable
export { StatusPill }